import {
	CampaignViewModel,
	ContactFilterCriteriaProperty,
	DefaultBulkSendExcludedTags,
	FilterOperator,
	ICampaign,
	VmUtils,
} from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AdvancedTagFilterCollection } from '../../../../../models';
import { isTagSearchContactFilterCriteria } from '../../../../../models/UiUtils';
import { ComposeEmailViewModel } from '../../../../../viewmodels/AppViewModels';
import { Tags } from '../../../../containers/reporting/Campaigns/Tags';
import { baseStyleSheet } from '../../../../styles/styles';
import { Tag } from '../../../tags/Tag';
import { styleSheet } from './styles';

interface IProps {
	children?(tags: React.ReactNode): React.ReactNode;
	className?: string;
	onRenderFallback?(): React.ReactNode;
	styles?: StyleDeclarationValue[];
	tagPrefixStyles?: StyleDeclarationValue[];
	tags?: string[];
	viewModel?: CampaignViewModel | ComposeEmailViewModel;
	campaign?: ICampaign;
}

class _GroupEmailTags extends React.Component<IProps> {
	constructor(props: IProps) {
		super(props);

		makeObservable<_GroupEmailTags, 'tagFilters'>(this, {
			tagFilters: computed,
		});
	}

	public render() {
		const { className, styles, onRenderFallback, tags, children, tagPrefixStyles } = this.props;
		const tagFilters = this.tagFilters;
		const hasTagFilters = !!tagFilters && tagFilters.length > 0;
		const hasExplicitTags = !!tags && tags.length > 0;
		if (!hasTagFilters && !hasExplicitTags) {
			if (onRenderFallback) {
				return onRenderFallback();
			}
			return null;
		}

		const sortedFilters = [
			...(tagFilters?.filters ?? []).filter(filter => filter.type === FilterOperator.Or),
			...(tagFilters?.filters ?? []).filter(filter => filter.type === FilterOperator.And),
			...(tagFilters?.filters ?? []).filter(filter => filter.type === FilterOperator.Not),
		];

		const content = (
			<div className={`${css(styleSheet.container, ...(styles || []))} group-email-tags ${className || ''}`}>
				{hasTagFilters ? (
					sortedFilters
						.filter(x => !DefaultBulkSendExcludedTags.has(x.tag))
						.map((x, i) => {
							return (
								<React.Fragment key={x.id}>
									{i > 0 && (
										<span className={css(styleSheet.tagTokenOpPrefix, ...(tagPrefixStyles || []))}>
											{x.type === FilterOperator.Or ? 'or' : x.type === FilterOperator.Not ? 'not' : 'and'}
										</span>
									)}
									<Tag
										styles={[
											baseStyleSheet.truncateText,
											styleSheet.tagToken,
											x.type === FilterOperator.Not ? styleSheet.tagTokenExclude : null,
										]}
										tagValue={x.tag}
									/>
								</React.Fragment>
							);
						})
				) : (
					<Tags tags={tags} maxTags={3} />
				)}
			</div>
		);
		return children ? children(content) : content;
	}

	private get tagFilters() {
		const { viewModel, campaign } = this.props;
		if (
			(viewModel instanceof CampaignViewModel && viewModel?.filterRequest?.criteria) ||
			campaign?.filterRequest?.criteria
		) {
			const criteria = (viewModel as CampaignViewModel)?.filterRequest?.criteria ?? campaign?.filterRequest?.criteria;
			const sorted = VmUtils.sortContactFilterCriteria(criteria);
			const isAllTagsFiltersExceptAllFilter = sorted.searches.filter(
				x => x.property !== ContactFilterCriteriaProperty.All
			);
			if (sorted.searches.length > 0 && isAllTagsFiltersExceptAllFilter) {
				const filters = AdvancedTagFilterCollection.instanceWithCriteria(sorted.searches);
				filters.sort();
				return filters;
			}
		}
		if (
			viewModel instanceof ComposeEmailViewModel &&
			viewModel?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria
		) {
			const sorted = VmUtils.sortContactFilterCriteria(
				viewModel.emailMessage.contactsFilterRequest.contactFilterRequest.criteria
			);
			const isAllTagsFiltersExceptAllFilter = sorted.searches
				.filter(x => x.property !== ContactFilterCriteriaProperty.All)
				.every(x => isTagSearchContactFilterCriteria(x));
			if (sorted.searches.length > 0 && isAllTagsFiltersExceptAllFilter) {
				const filters = AdvancedTagFilterCollection.instanceWithCriteria(sorted.searches);
				filters.sort();
				return filters;
			}
		}
		return null;
	}
}

export const GroupEmailTags = observer(_GroupEmailTags);
